import { Fragment, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PanelLoading } from "../../../components/Loading";
import { useToast } from "../../../components/Toast";
import { useAuth } from "../../../auth/AuthContext";
import { formatDate } from "../../../lib/format";
import type { ApiError } from "../../../api/client";
import {
  retainedSampleApi,
  type RetainedSample,
  type RetainedState,
  type RetainedTally,
} from "../../../api/retainedSamples";
import RetainedSampleForm from "./RetainedSampleForm";
import { retainedErrorMessage } from "./retainedSampleErrors";

/**
 * Kontrauzorci.
 *
 * Ekran odgovara na pitanje koje laborant stvarno ima pred policom: koji uzorci mi još stoje i kojima
 * rok ističe. Zato je spisak poređan po roku, najbliži prvi, a ne po danu unosa.
 *
 * Zašto se uopšte vodi: kad kupac tvrdi da roba nije ono što je nalaz rekao, spor se rešava
 * superanalizom — ponovnim merenjem istog materijala. Bez tegle se spor rešava na papiru.
 */

/** Rok koji je prošao se ne saopštava isto kao rok koji dolazi. */
const TAG_BY_STATE: Record<RetainedState, string> = {
  EXPIRED: "err",
  DUE_SOON: "warn",
  KEPT: "ok",
  NO_DEADLINE: "neutral",
};

export default function RetainedSamplesPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const lang = i18n.resolvedLanguage ?? "sr";
  const smeDaVodi = hasPermission("retained_samples:manage");
  const canDelete = hasPermission("section:retained_samples:full");

  const [rows, setRows] = useState<RetainedSample[] | null>(null);
  const [tally, setTally] = useState<RetainedTally | null>(null);
  const [state, setState] = useState<"" | RetainedState>("");
  const [q, setQ] = useState("");
  const [takenFrom, setTakenFrom] = useState("");
  const [takenTo, setTakenTo] = useState("");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [keepUntil, setKeepUntil] = useState("");
  const [location, setLocation] = useState("");

  /** Poznata oznaka greške se saopštava na srpskom; nepoznata ostaje kako ju je server objasnio. */
  const greska = useCallback(
    (e: ApiError) => {
      const prevod = retainedErrorMessage(e);
      return prevod ? t(prevod[0], prevod[1]) : e.message;
    },
    [t],
  );

  const load = useCallback(async () => {
    setRows(null);
    try {
      setRows(
        await retainedSampleApi.search({
          state: state || undefined,
          q: q.trim() || undefined,
          takenFrom: takenFrom || undefined,
          takenTo: takenTo || undefined,
        }),
      );
    } catch (e) {
      toast.error(greska(e as ApiError));
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, q, takenFrom, takenTo]);

  /** Brojevi se čitaju posebno, nad celim skladištem: filtrirani spisak ne sme da ih smanji. */
  const loadTally = useCallback(async () => {
    try {
      setTally(await retainedSampleApi.tally());
    } catch {
      setTally(null);
    }
  }, []);

  useEffect(() => {
    void load();
    void loadTally();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  function stateLabel(s: RetainedState): string {
    if (s === "EXPIRED") return t("retained.state.EXPIRED", "rok prošao");
    if (s === "DUE_SOON") return t("retained.state.DUE_SOON", "ističe");
    if (s === "NO_DEADLINE") return t("retained.state.NO_DEADLINE", "bez roka");
    return t("retained.state.KEPT", "u roku");
  }

  function subjectLabel(s: RetainedSample): string {
    const vrsta =
      s.subject === "DELIVERY"
        ? t("retained.subject.DELIVERY", "isporuke")
        : s.subject === "RECEIVING_LOT"
          ? t("retained.subject.RECEIVING_LOT", "prijemne šarže")
          : t("retained.subject.GOODS_LOT", "LOT-a gotove robe");
    return s.subjectLabel ? `${vrsta} — ${s.subjectLabel}` : vrsta;
  }

  /** „11 dana" ili „11 dana prekoračeno" — isti broj, dva različita saopštenja. */
  function daysLabel(s: RetainedSample): string {
    if (s.daysLeft === null) return "—";
    if (s.daysLeft < 0) return t("retained.daysOver", "{{n}} dana prekoračeno", { n: Math.abs(s.daysLeft) });
    return t("retained.daysLeft", "{{n}} dana", { n: s.daysLeft });
  }

  function startEdit(s: RetainedSample) {
    setEditing(s.id);
    setKeepUntil(s.keepUntil ?? "");
    setLocation(s.location ?? "");
  }

  /**
   * Prazan datum znači „skini rok", i tako se i šalje.
   *
   * Server polje koje nije poslato ostavlja kako je bilo, pa bi prazan datum bez `clearKeepUntil` prošao
   * bez ikakve izmene a ekran bi rekao da je uzorak izmenjen. Uzorak bez roka nema dan za bacanje — to je
   * stanje koje se traži izričito, ne nešto što ispadne iz praznog polja.
   */
  async function saveEdit(s: RetainedSample) {
    try {
      await retainedSampleApi.update(s.id, {
        keepUntil: keepUntil || null,
        clearKeepUntil: !keepUntil,
        location: location.trim() || null,
      });
      toast.success(t("retained.updated", "Uzorak je izmenjen."));
      setEditing(null);
      void load();
      void loadTally();
    } catch (e) {
      toast.error(greska(e as ApiError));
    }
  }

  async function discard(s: RetainedSample) {
    if (!window.confirm(t("retained.confirmDiscard", "Uzorak {{n}} je bačen?", { n: s.label }))) return;
    try {
      await retainedSampleApi.discard(s.id);
      toast.success(t("retained.discarded", "Uzorak je sklonjen sa spiska."));
      void load();
      void loadTally();
    } catch (e) {
      toast.error(greska(e as ApiError));
    }
  }

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("retained.title", "Kontrauzorci")}</h1>
          <div className="sub">
            {t(
              "retained.lead",
              "Šta je od robe zadržano i do kada se drži. Superanaliza u sporu se radi na kontrauzorku — bez njega se spor rešava na papiru.",
            )}
          </div>
        </div>
        {smeDaVodi && !adding && (
          <button type="button" className="btn primary" onClick={() => setAdding(true)}>
            {t("retained.new", "Nov uzorak")}
          </button>
        )}
      </div>

      {smeDaVodi && adding && (
        <RetainedSampleForm
          onCancel={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            void load();
            void loadTally();
          }}
        />
      )}

      {tally && (
        <div className="kpi-grid">
          <div className="card">
            <div className="card-b">
              <div className="muted">{t("retained.kpi.total", "uzoraka se drži")}</div>
              <strong style={{ fontSize: 22 }}>{tally.total}</strong>
            </div>
          </div>
          <div className="card">
            <div className="card-b">
              <div className="muted">{t("retained.kpi.expired", "rok prošao")}</div>
              <strong style={{ fontSize: 22 }}>{tally.expired}</strong>
            </div>
          </div>
          <div className="card">
            <div className="card-b">
              <div className="muted">{t("retained.kpi.dueSoon", "ističe u 30 dana")}</div>
              <strong style={{ fontSize: 22 }}>{tally.dueSoon}</strong>
            </div>
          </div>
          <div className="card">
            <div className="card-b">
              <div className="muted">{t("retained.kpi.withoutDeadline", "bez roka")}</div>
              <strong style={{ fontSize: 22 }}>{tally.withoutDeadline}</strong>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-b">
          <div className="toolbar" style={{ flexWrap: "wrap", gap: 8 }}>
            <div className="seg" role="group">
              {(
                [
                  ["", t("retained.filter.all", "sve")],
                  ["EXPIRED", t("retained.filter.expired", "rok prošao")],
                  ["DUE_SOON", t("retained.filter.dueSoon", "ističe u 30 dana")],
                  ["KEPT", t("retained.filter.kept", "u roku")],
                  ["NO_DEADLINE", t("retained.filter.noDeadline", "bez roka")],
                ] as ["" | RetainedState, string][]
              ).map(([k, naziv]) => (
                <button key={k || "all"} type="button" aria-pressed={state === k} onClick={() => setState(k)}>
                  {naziv}
                </button>
              ))}
            </div>
            <input
              className="input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("retained.search", "oznaka ili mesto")}
            />
            <input
              className="input"
              type="date"
              value={takenFrom}
              onChange={(e) => setTakenFrom(e.target.value)}
            />
            <input className="input" type="date" value={takenTo} onChange={(e) => setTakenTo(e.target.value)} />
            <button type="button" className="btn sm" onClick={() => void load()}>
              {t("common.search", "Traži")}
            </button>
          </div>
        </div>
      </div>

      {rows === null ? (
        <PanelLoading text={t("retained.loading", "Čitam uzorke…")} />
      ) : rows.length === 0 ? (
        <div className="empty">{t("retained.none", "Nema uzoraka za ove uslove.")}</div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>{t("retained.col.label", "Oznaka")}</th>
              <th>{t("retained.col.takenOn", "Uzet")}</th>
              <th>{t("retained.col.keepUntil", "Drži se do")}</th>
              <th>{t("retained.col.left", "Ostalo")}</th>
              <th>{t("retained.col.location", "Mesto")}</th>
              <th>{t("retained.col.subject", "Uzet sa")}</th>
              {smeDaVodi && <th />}
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <Fragment key={s.id}>
                <tr>
                  <td>
                    <strong>{s.label}</strong>
                  </td>
                  <td>{formatDate(s.takenOn, lang)}</td>
                  <td>
                    <span className={`tag ${TAG_BY_STATE[s.state]}`}>
                      {s.keepUntil ? formatDate(s.keepUntil, lang) : stateLabel(s.state)}
                    </span>
                  </td>
                  <td className={s.state === "EXPIRED" ? undefined : "muted"}>{daysLabel(s)}</td>
                  <td>{s.location ?? <span className="muted">—</span>}</td>
                  <td className="muted">{subjectLabel(s)}</td>
                  {smeDaVodi && (
                    <td>
                      <button type="button" className="btn ghost sm" onClick={() => startEdit(s)}>
                        {t("retained.edit", "Ispravi")}
                      </button>
                      <button type="button" className="btn ghost sm" disabled={!canDelete} onClick={() => void discard(s)}>
                        {t("retained.discard", "Bačen")}
                      </button>
                    </td>
                  )}
                </tr>
                {s.note && (
                  <tr>
                    <td colSpan={smeDaVodi ? 7 : 6} className="muted">
                      {s.note}
                    </td>
                  </tr>
                )}
                {smeDaVodi && editing === s.id && (
                  <tr>
                    <td colSpan={smeDaVodi ? 7 : 6}>
                      <div className="toolbar" style={{ flexWrap: "wrap", gap: 8 }}>
                        <label>
                          <span className="muted">{t("retained.keepUntil", "Drži se do")}</span>
                          <input
                            className="input"
                            type="date"
                            value={keepUntil}
                            onChange={(e) => setKeepUntil(e.target.value)}
                          />
                        </label>
                        <label>
                          <span className="muted">{t("retained.location", "Mesto")}</span>
                          <input
                            className="input"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                          />
                        </label>
                        <button type="button" className="btn sm primary" onClick={() => void saveEdit(s)}>
                          {t("common.save", "Sačuvaj")}
                        </button>
                        <button type="button" className="btn ghost sm" onClick={() => setEditing(null)}>
                          {t("common.cancel", "Otkaži")}
                        </button>
                      </div>
                      <div className="muted" style={{ marginTop: 6 }}>
                        {t(
                          "retained.editHint",
                          "Prazan rok znači da uzorak nema dan za bacanje. Bačena tegla se skida sa spiska posebnom radnjom.",
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
